/**
 * StartMenu — first-paint overlay before the stage ticks.
 *
 * Lives as a standalone DOM module because it renders before CellStage
 * (and therefore before ui.css) is loaded. Styles are injected as a
 * dedicated <style> tag. Returns a Promise that resolves once the user
 * picks Continue, New Game, or a level jump.
 */

import { saveManager } from '@/core/SaveManager';
import { VERSION } from '@/version';
import { openScoreboard } from './ScoreboardPanel';
import { formatTime, formatProgress } from '@/score/format';
import { tierOf } from '@/score/formula';

export type StartAction = 'continue' | 'new';

export function showStartMenu(): Promise<StartAction> {
  return new Promise((resolve) => {
    injectStyles();
    const overlay = document.createElement('div');
    overlay.id = 'start-menu';
    overlay.innerHTML = `
      <div class="sm-bg"></div>
      <div class="sm-orb"></div>
      <div class="sm-card">
        <div class="sm-sub">A microbial epic</div>
        <h1 class="sm-title">EVOLVE</h1>
        <div class="sm-meta" id="sm-meta"></div>
        <div class="sm-actions">
          <button class="sm-btn primary" data-act="continue">Continue</button>
          <button class="sm-btn" data-act="new">New Game</button>
        </div>
        <div class="sm-actions sm-actions-small" style="margin-top:12px">
          <button class="sm-btn ghost" data-act="scores">🏆 Scoreboard</button>
        </div>
        <div class="sm-divider"><span>jump to level</span></div>
        <div class="sm-actions sm-actions-small sm-stage-row">
          <button class="sm-btn ghost" data-stage="multicell">Multicell</button>
          <button class="sm-btn ghost" data-stage="cambrian" title="Level 3 is in alpha — expect rough edges">
            Cambrian <span class="sm-alpha">alpha</span>
          </button>
        </div>
        <div class="sm-note">Practice only — not saved, not scored · Level 3 · Cambrian is in alpha</div>
        <div class="sm-version">${VERSION}</div>
      </div>
    `;
    document.body.appendChild(overlay);
    document.body.classList.add('show-cursor');
    requestAnimationFrame(() => overlay.classList.add('visible'));

    const metaEl = overlay.querySelector('#sm-meta') as HTMLElement;
    const continueBtn = overlay.querySelector('[data-act="continue"]') as HTMLButtonElement;
    const newBtn = overlay.querySelector('[data-act="new"]') as HTMLButtonElement;

    const refresh = () => {
      const has = saveManager.hasRunProgress();
      const run = saveManager.getRun();
      const player = saveManager.getPlayer();
      const best = player.best;
      continueBtn.disabled = !has;
      continueBtn.classList.toggle('disabled', !has);
      const bestText = best.cellMaxBiomass > 0
        ? formatProgress(tierOf(best), best.cellMaxBiomass, best.multicellMaxBiomass)
        : '—';
      metaEl.innerHTML =
        `<span>Player <b>${escapeHtml(player.playerName)}</b></span>` +
        `<span>Best <b>${escapeHtml(bestText)}</b></span>` +
        (has
          ? `<span>Biomass <b>${Math.floor(run.biomass).toLocaleString()}</b></span>` +
            `<span>Time <b>${formatTime(run.scoreTime)}</b></span>`
          : '');
    };
    refresh();

    // Detach hooks accumulate during setup; dismiss runs them all so we
    // never leak window-level listeners after the overlay tears down.
    const detachers: Array<() => void> = [];
    const dismiss = (action: StartAction) => {
      for (const d of detachers) d();
      detachers.length = 0;
      overlay.classList.remove('visible');
      document.body.classList.remove('show-cursor');
      setTimeout(() => overlay.remove(), 500);
      resolve(action);
    };

    continueBtn.addEventListener('click', () => {
      if (continueBtn.disabled) return;
      dismiss('continue');
    });
    newBtn.addEventListener('click', () => {
      if (saveManager.hasRunProgress()) {
        const ok = confirm('Start a new run? Your biomass, DNA, mutations, and upgrades will be reset. Your player name and best score are kept.');
        if (!ok) return;
      }
      saveManager.resetPlaythroughProgress(true);
      saveManager.resetRunProgress('cell');
      dismiss('new');
    });
    (overlay.querySelector('[data-act="scores"]') as HTMLButtonElement)
      .addEventListener('click', () => openScoreboard());

    // Level jump (level 2 and up): plays that level in a sandbox. It never
    // touches the scoreboard or the saved run — Continue (after a reload)
    // still resumes the real run, and the player name + best are kept.
    const stageBtns = overlay.querySelectorAll<HTMLButtonElement>('[data-stage]');
    stageBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const stageId = btn.dataset.stage;
        if (!stageId) return;
        const alphaNote = stageId === 'cambrian'
          ? 'Level 3 (Cambrian) is in alpha — expect rough edges.\n\n'
          : '';
        const keepNote = saveManager.hasRunProgress()
          ? ' Your saved run is kept — press Continue next time to resume it.'
          : '';
        const ok = confirm(
          `${alphaNote}Jump to "${stageId}" for practice?${keepNote}\n\n` +
          `Jumped levels aren't saved and don't count on the scoreboard.`,
        );
        if (!ok) return;
        saveManager.startJump(stageId);
        dismiss('new');
      });
    });

    // Hidden cheat: typing "phase3" quickly while the menu is up jumps
    // straight into the Cambrian stage. Stage 3 is otherwise unlisted
    // because it isn't ready for general play yet. Matches the in-game
    // "food" cheat pattern (≤600 ms gap between keys).
    const CHEAT_WORD = 'phase3';
    const CHEAT_MAX_GAP_MS = 600;
    let cheatBuf = '';
    let cheatLastTs = 0;
    const cheatHandler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key.length !== 1) return;
      const now = performance.now();
      if (now - cheatLastTs > CHEAT_MAX_GAP_MS) cheatBuf = '';
      cheatLastTs = now;
      cheatBuf = (cheatBuf + e.key.toLowerCase()).slice(-CHEAT_WORD.length);
      if (cheatBuf !== CHEAT_WORD) return;
      cheatBuf = '';
      saveManager.startJump('cambrian');
      dismiss('new');
    };
    window.addEventListener('keydown', cheatHandler);
    detachers.push(() => window.removeEventListener('keydown', cheatHandler));
  });
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

function injectStyles(): void {
  if (document.getElementById('sm-styles')) return;
  const style = document.createElement('style');
  style.id = 'sm-styles';
  style.textContent = `
#start-menu {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #e4ecf7;
  opacity: 0;
  transition: opacity 0.5s ease;
  font-family: 'Inter', system-ui, sans-serif;
}
#start-menu.visible { opacity: 1; }

#start-menu .sm-bg {
  position: absolute; inset: 0;
  background:
    radial-gradient(ellipse at 30% 20%, rgba(79, 209, 197, 0.18), transparent 55%),
    radial-gradient(ellipse at 75% 80%, rgba(183, 148, 244, 0.18), transparent 55%),
    linear-gradient(160deg, #020510 0%, #040a1a 100%);
}

#start-menu .sm-orb {
  position: absolute;
  width: 520px; height: 520px;
  border-radius: 50%;
  left: 50%; top: 50%;
  transform: translate(-50%, -50%);
  background: radial-gradient(circle at 35% 35%, rgba(79, 209, 197, 0.55), rgba(79, 209, 197, 0.08) 55%, transparent 70%);
  filter: blur(28px);
  animation: smBreath 7s ease-in-out infinite;
  pointer-events: none;
}
@keyframes smBreath {
  0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.9; }
  50%      { transform: translate(-50%, -50%) scale(1.08); opacity: 1; }
}

#start-menu .sm-card {
  position: relative;
  z-index: 1;
  padding: 46px 52px 32px;
  border-radius: 32px;
  background: rgba(10, 16, 30, 0.72);
  border: 1px solid rgba(79, 209, 197, 0.25);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  box-shadow: 0 40px 120px rgba(0, 0, 0, 0.55);
  text-align: center;
  min-width: 320px;
  max-width: 92vw;
}

#start-menu .sm-sub {
  font-size: 11px;
  letter-spacing: 0.4em;
  text-transform: uppercase;
  opacity: 0.55;
  margin-bottom: 6px;
}
#start-menu .sm-title {
  margin: 0 0 14px;
  font-size: 56px;
  font-weight: 800;
  letter-spacing: 0.18em;
  color: #4FD1C5;
  text-shadow: 0 0 24px rgba(79, 209, 197, 0.5);
}

#start-menu .sm-meta {
  display: flex;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;
  font-size: 12px;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  opacity: 0.72;
  margin-bottom: 24px;
}
#start-menu .sm-meta b { color: #fff; font-weight: 600; letter-spacing: 0.05em; }
#start-menu .sm-meta .sm-empty { opacity: 0.5; letter-spacing: 0.1em; }

#start-menu .sm-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
}
#start-menu .sm-actions-small { margin-top: 4px; }

#start-menu .sm-btn {
  appearance: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  font-weight: 600;
  padding: 12px 22px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.04);
  color: #e4ecf7;
  transition: transform 0.12s, background 0.2s, border-color 0.2s, opacity 0.2s;
}
#start-menu .sm-btn:hover:not(:disabled) { transform: translateY(-1px); background: rgba(255, 255, 255, 0.09); }
#start-menu .sm-btn.primary {
  background: linear-gradient(135deg, #4FD1C5 0%, #38B2AC 100%);
  color: #02121a;
  border-color: transparent;
  box-shadow: 0 6px 18px rgba(79, 209, 197, 0.35);
}
#start-menu .sm-btn.primary:hover:not(:disabled) { box-shadow: 0 10px 24px rgba(79, 209, 197, 0.5); }
#start-menu .sm-btn.ghost { font-size: 11px; padding: 8px 16px; opacity: 0.8; }
#start-menu .sm-btn:disabled { opacity: 0.35; cursor: not-allowed; }
#start-menu .sm-btn.flash { background: rgba(79, 209, 197, 0.25); border-color: rgba(79, 209, 197, 0.6); }
#start-menu .sm-stage-row .sm-btn { font-size: 10px; padding: 7px 14px; letter-spacing: 0.12em; }
#start-menu .sm-alpha {
  display: inline-block; margin-left: 6px; padding: 2px 6px; border-radius: 999px;
  font-size: 8px; letter-spacing: 0.1em; vertical-align: 1px;
  background: rgba(250, 223, 124, 0.16); color: #fadf7c; border: 1px solid rgba(250, 223, 124, 0.45);
}
#start-menu .sm-note { margin-top: 8px; font-size: 10px; letter-spacing: 0.08em; opacity: 0.5; }

#start-menu .sm-divider {
  display: flex; align-items: center; gap: 10px;
  margin: 22px 0 10px;
  font-size: 10px; letter-spacing: 0.3em; text-transform: uppercase; opacity: 0.45;
}
#start-menu .sm-divider::before,
#start-menu .sm-divider::after {
  content: ""; flex: 1; height: 1px; background: rgba(255, 255, 255, 0.12);
}

#start-menu .sm-version {
  margin-top: 22px;
  font-size: 10px;
  letter-spacing: 0.2em;
  opacity: 0.35;
}

@media (max-width: 520px), (max-height: 500px) {
  #start-menu .sm-card { padding: 32px 24px 22px; }
  #start-menu .sm-title { font-size: 42px; }
  #start-menu .sm-orb { width: 360px; height: 360px; }
}
/* Landscape phone: the card is taller than the screen, so the stage
 * buttons fall off the bottom. Tighten it and let it scroll. */
@media (max-height: 500px) {
  #start-menu .sm-card {
    padding: 16px 24px 14px;
    border-radius: 22px;
    max-height: 94dvh;
    overflow-y: auto;
  }
  #start-menu .sm-title { font-size: 30px; }
  #start-menu .sm-sub { font-size: 10px; }
  #start-menu .sm-meta { margin: 6px 0; font-size: 11px; }
  #start-menu .sm-btn { padding: 9px 18px; font-size: 13px; }
  #start-menu .sm-divider { margin: 10px 0 8px; font-size: 9px; }
  #start-menu .sm-note { margin-top: 8px; font-size: 9px; }
  #start-menu .sm-orb { width: 260px; height: 260px; }
}
`;
  document.head.appendChild(style);
}
